import { useState, useEffect, useCallback } from 'react';
import { TenantUser } from '../types';
import { getClientUsers } from '../services/api';

// Reserved route segments that should not be treated as client IDs
const RESERVED_ROUTES = ['onboard', 'new', 'create', 'edit'];

interface UseUsersResult {
  users: TenantUser[];
  userCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useUsers(clientId: string | undefined): UseUsersResult {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!clientId || RESERVED_ROUTES.includes(clientId.toLowerCase())) {
      setUsers([]);
      setUserCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getClientUsers(clientId);
      setUsers(response.users);
      setUserCount(response.userCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      setUsers([]);
      setUserCount(0);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    userCount,
    loading,
    error,
    refetch: fetchUsers,
  };
}
